import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  SwipeableDrawer,
  Grow,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CalendarMonthOutlined,
  ChevronLeftRounded,
  ChevronRightRounded,
  CloseRounded,
} from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { EconomicCalendarEvent } from "../../hooks/useEconomicCalendar";
import { useTouchLayout } from "../../hooks/useTouchLayout";
import { isNative } from "../../lib/platform";
import EconomicEventCard from "./EconomicEventCard";
import { hapticSelection } from "../../lib/haptics";
import { focusRingSx, panelBorder, riseIn, overlaySurface } from "./overviewPanel";
import { touchHitAreaSx } from "./EarningsCalendar/touchHitArea";
import {
  IMPACT_COLORS,
  IMPACT_TIERS,
  ImpactTier,
  calendarWeeks,
  eventsByTier,
  resolveTiers,
  tierCounts,
  weekAnchor,
} from "./economicCalendarSelection";

dayjs.extend(utc);

// The full economic calendar. The dashboard panel is a four-slot glance;
// this is where you read the week — a wide centred panel on desktop that
// grows out of the page and leaves it legible behind (the chat's
// PassagesModal idiom), true fullscreen on phones.
//
// ONE header row, then the body. The header carries the name, the week
// strip and the way out on a single line: the strip is one week of small
// floating day pills, Monday to Friday plus any weekend day that has
// something; the arrows page by week and the strip slides. The body opens on the day, named in full,
// with the tier chips that decide what of it is on screen, and then one
// section per tier that is on — HIGH, then MEDIUM, then LOW, each in the
// order it happens. The order IS the hierarchy: highs are the day, the
// rest is context. No rules anywhere in the body: the header's hairline is
// the only line, so nothing below it can read as a second header.
//
// High is always on. Medium and Low are the user's picks and travel with
// them between days and between launches (a per-device preference, the
// watchlist view-prefs idiom); a day where the picks show nothing escalates
// one tier at a time until something is on screen, and the sub-line says
// so. A tier that is off does not exist on the page — the chips are the one
// control.

interface EconomicCalendarModalProps {
  open: boolean;
  grouped: Record<string, EconomicCalendarEvent[]> | undefined;
  dateRange: { from: string; to: string } | undefined;
  /** The day to open on — the panel's lead day. */
  initialDate: string;
  /** The UTC calendar date, the same clock the server grouped by. */
  today: string;
  onClose: () => void;
}

type SlideDirection = "forward" | "back" | null;

// The strip's slide when the week changes. Transform/opacity only, off
// under reduced motion — the dashboard's own entrance vocabulary.
const slideInSx = (direction: SlideDirection) => {
  if (!direction) return {};
  const from = direction === "forward" ? "16px" : "-16px";
  return {
    animation: "econWeekSlide 0.3s cubic-bezier(0.22, 1, 0.36, 1) both",
    "@keyframes econWeekSlide": {
      from: { opacity: 0, transform: `translateX(${from})` },
      to: { opacity: 1, transform: "none" },
    },
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  } as const;
};

const hairlineOf = (isDark: boolean): string =>
  isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.05)";

// The picked tiers, persisted per device. Storage is input, never truth:
// anything that is not a pickable tier name is dropped, so a stale or
// hand-edited value can only ever fall back to the default (High only).
const TIERS_STORAGE_KEY = "insight:econ-calendar-tiers";
const PICKABLE_TIERS = new Set<string>(["Medium", "Low"]);

const readStoredTiers = (): Set<ImpactTier> => {
  try {
    const raw = localStorage.getItem(TIERS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter(
        (tier): tier is ImpactTier =>
          typeof tier === "string" && PICKABLE_TIERS.has(tier),
      ),
    );
  } catch {
    // Storage unavailable (private mode) — the picks just won't persist.
    return new Set();
  }
};

const writeStoredTiers = (tiers: ReadonlySet<ImpactTier>) => {
  try {
    localStorage.setItem(TIERS_STORAGE_KEY, JSON.stringify(Array.from(tiers)));
  } catch {
    // The picks just won't survive to the next launch.
  }
};

// A day, small enough to live in the header row: the weekday over the
// date, nothing else — the day's shape is the body's job.
const DAY_PILL_HEIGHT = 36;
const TIER_CHIP_HEIGHT = 28;
const WEEK_ARROW_SIZE = 24;

const DayPill: React.FC<{
  date: string;
  isToday: boolean;
  selected: boolean;
  compact: boolean;
  /** Touch layouts grow the hit area to 44px without growing the pill. */
  touch: boolean;
  onSelect: (date: string) => void;
}> = ({ date, isToday, selected, compact, touch, onSelect }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const day = dayjs.utc(date);
  const tint = isDark ? "rgba(34, 197, 94, 0.08)" : "rgba(34, 197, 94, 0.06)";

  return (
    <Box
      component="button"
      onClick={() => onSelect(date)}
      aria-pressed={selected}
      aria-label={`${day.format("dddd D MMMM")}${isToday ? ", today" : ""}`}
      sx={{
        all: "unset",
        ...focusRingSx,
        ...(touch && touchHitAreaSx(DAY_PILL_HEIGHT)),
        boxSizing: "border-box",
        cursor: "pointer",
        fontFamily: "inherit",
        minWidth: compact ? 40 : 52,
        minHeight: DAY_PILL_HEIGHT,
        px: compact ? 0.5 : 0.75,
        py: 0.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.25,
        borderRadius: 1,
        border: `1px solid ${selected ? "rgba(34, 197, 94, 0.45)" : "transparent"}`,
        backgroundColor: selected ? tint : "transparent",
        WebkitTapHighlightColor: "transparent",
        transition: "background-color 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          backgroundColor: selected
            ? tint
            : isDark
              ? "rgba(255, 255, 255, 0.04)"
              : "rgba(0, 0, 0, 0.04)",
        },
        "&:active": { transform: "scale(0.97)" },
      }}
    >
      <Typography
        sx={{
          fontSize: "0.5625rem",
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          lineHeight: 1,
          color: isToday || selected ? "#22c55e" : theme.palette.text.secondary,
          transition: "color 0.2s ease",
        }}
      >
        {isToday ? "Today" : day.format("ddd")}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 700,
          lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
          color: selected ? "#22c55e" : theme.palette.text.primary,
          transition: "color 0.2s ease",
        }}
      >
        {day.format("D")}
      </Typography>
    </Box>
  );
};

const TierChip: React.FC<{
  tier: ImpactTier;
  count: number;
  on: boolean;
  /** High is the baseline and never toggles; an escalated tier is on for
   *  the day whether or not it was picked, so it does not toggle either. */
  locked: boolean;
  /** Touch layouts grow the hit area to 44px without growing the chip. */
  touch: boolean;
  onToggle: (tier: ImpactTier) => void;
}> = ({ tier, count, on, locked, touch, onToggle }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const interactive = !locked;

  return (
    <Box
      component="button"
      onClick={() => interactive && onToggle(tier)}
      aria-pressed={on}
      aria-disabled={locked}
      aria-label={`${tier} impact, ${count} events`}
      sx={{
        all: "unset",
        ...focusRingSx,
        ...(touch && touchHitAreaSx(TIER_CHIP_HEIGHT)),
        boxSizing: "border-box",
        cursor: interactive ? "pointer" : "default",
        fontFamily: "inherit",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        height: TIER_CHIP_HEIGHT,
        px: 1,
        borderRadius: 0.75,
        fontSize: "0.75rem",
        fontWeight: on ? 600 : 500,
        color: on ? theme.palette.text.primary : theme.palette.text.secondary,
        backgroundColor: on
          ? isDark
            ? "rgba(255, 255, 255, 0.06)"
            : "rgba(0, 0, 0, 0.05)"
          : "transparent",
        WebkitTapHighlightColor: "transparent",
        transition: "color 0.2s ease, background-color 0.2s ease",
        ...(interactive && {
          "&:hover": { color: theme.palette.text.primary },
          "&:active": { transform: "scale(0.97)" },
        }),
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: IMPACT_COLORS[tier],
          opacity: on ? 1 : 0.4,
          transition: "opacity 0.2s ease",
        }}
      />
      {tier}
      <Typography
        component="span"
        sx={{
          fontSize: "0.6875rem",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          color: theme.palette.text.disabled,
        }}
      >
        {count}
      </Typography>
    </Box>
  );
};

// A tier's events under a label line — the dashboard's own section idiom, a
// label sitting directly on the page rather than a boxed rail.
const TierSection: React.FC<{
  tier: ImpactTier;
  events: EconomicCalendarEvent[];
  pulse: boolean;
}> = ({ tier, events, pulse }) => {
  const theme = useTheme();

  return (
    <Box sx={{ ...riseIn(0), mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: IMPACT_COLORS[tier],
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontSize: "0.625rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            lineHeight: 1,
            color: theme.palette.text.secondary,
            whiteSpace: "nowrap",
          }}
        >
          {tier} impact
        </Typography>
        <Typography
          sx={{
            fontSize: "0.625rem",
            fontWeight: 600,
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
            color: theme.palette.text.disabled,
          }}
        >
          {events.length}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(auto-fill, minmax(280px, 1fr))",
          },
          gap: 1.25,
        }}
      >
        {events.map((event, index) => (
          <EconomicEventCard
            key={`${event.date}-${event.country}-${event.event}-${index}`}
            event={event}
            pulse={pulse}
          />
        ))}
      </Box>
    </Box>
  );
};

const EconomicCalendarModal: React.FC<EconomicCalendarModalProps> = ({
  open,
  grouped,
  dateRange,
  initialDate,
  today,
  onClose,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  // Below md the header cannot hold the name, seven pills and the close on
  // one line; the strip takes its own row underneath instead.
  const stackHeader = useMediaQuery(theme.breakpoints.down("md"));
  const isTouch = useTouchLayout();
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const hairline = hairlineOf(isDark);

  const [selected, setSelected] = useState(initialDate);
  const [picked, setPicked] = useState<Set<ImpactTier>>(readStoredTiers);
  const [slide, setSlide] = useState<SlideDirection>(null);

  // Land on the panel's lead day each time the calendar opens — and only
  // then: the poll refreshing the data underneath must not yank the user
  // back to today mid-read. The tier picks are the user's and stay.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setSelected(initialDate);
      setSlide(null);
    }
    wasOpen.current = open;
  }, [open, initialDate]);

  const weeks = useMemo(
    () => (dateRange ? calendarWeeks(dateRange, grouped) : []),
    [dateRange, grouped],
  );
  const days = useMemo(() => weeks.flat(), [weeks]);
  // The panel's lead day defaults to today when the range holds nothing for
  // it, and today can sit outside the served range — that would leave the
  // strip empty with both arrows dead. Off-range, the calendar opens on the
  // range's first day instead.
  const activeDate = days.includes(selected) ? selected : (days[0] ?? selected);
  const weekIndex = weeks.findIndex((week) => week.includes(activeDate));
  const week = weeks[weekIndex] ?? [];
  const dayIndex = days.indexOf(activeDate);
  const canPageBack = weekIndex > 0;
  const canPageForward = weekIndex >= 0 && weekIndex < weeks.length - 1;

  const counts = useMemo(
    () => tierCounts(grouped?.[activeDate]),
    [grouped, activeDate],
  );
  const byTier = useMemo(
    () => eventsByTier(grouped?.[activeDate]),
    [grouped, activeDate],
  );
  const tiers = useMemo(() => resolveTiers(picked, counts), [picked, counts]);
  const total = IMPACT_TIERS.reduce((sum, tier) => sum + counts[tier], 0);
  const shown = IMPACT_TIERS.filter(
    (tier) => tiers.has(tier) && counts[tier] > 0,
  );
  const isToday = activeDate === today;

  // The tiers on screen that the user did not ask for — the day was too
  // quiet for their picks, so the calendar reached down a tier. Named in
  // the sub-line so the escalation never passes for a choice they made.
  const escalated = IMPACT_TIERS.filter(
    (tier) => tier !== "High" && tiers.has(tier) && !picked.has(tier),
  );

  const goTo = (date: string) => {
    if (date === activeDate) return;
    const targetWeek = weeks.findIndex((candidate) => candidate.includes(date));
    if (targetWeek !== weekIndex) {
      setSlide(targetWeek > weekIndex ? "forward" : "back");
    }
    hapticSelection();
    setSelected(date);
  };
  const pageWeek = (delta: 1 | -1) => {
    const target = weeks[weekIndex + delta];
    if (target) goTo(weekAnchor(target, today));
  };
  const stepDay = (delta: 1 | -1) => {
    const next = days[dayIndex + delta];
    if (next) goTo(next);
  };
  const toggleTier = (tier: ImpactTier) => {
    if (tier === "High") return;
    hapticSelection();
    setPicked((previous) => {
      const next = new Set(previous);
      if (next.has(tier)) {
        next.delete(tier);
      } else {
        next.add(tier);
      }
      // Write-through inside the updater keeps prev exact; a StrictMode
      // double-invoke just writes the same bytes twice.
      writeStoredTiers(next);
      return next;
    });
  };

  // A horizontal swipe across the day steps to the neighbour — the gesture
  // a phone expects of a paged calendar. Decisively horizontal only, so a
  // scroll through a busy day never turns a page by accident.
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    swipeStart.current = { x: touch.clientX, y: touch.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy) * 2) return;
    stepDay(dx < 0 ? 1 : -1);
  };

  const subLine = (() => {
    if (total === 0) return "Nothing scheduled";
    const count = `${total} ${total === 1 ? "event" : "events"}`;
    if (escalated.length === 0) return count;
    const missing = escalated.includes("Low")
      ? "high or medium-impact"
      : "high-impact";
    const showing = escalated.map((tier) => tier.toLowerCase()).join(" and ");
    return `${count} · No ${missing} events, so ${showing} is shown`;
  })();

  const arrowSx = {
    width: WEEK_ARROW_SIZE,
    height: WEEK_ARROW_SIZE,
    flexShrink: 0,
    color: theme.palette.text.disabled,
    transition: "color 0.15s ease",
    "&:hover": { color: theme.palette.text.primary },
    // Fade rather than vanish at the ends — a control that disappears makes
    // the panel feel like it broke.
    "&.Mui-disabled": { color: theme.palette.text.disabled, opacity: 0.3 },
  } as const;

  const strip = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        minWidth: 0,
        // Stacked under the name, the strip spans the row; in the header
        // line it sits at its natural width, centred on the panel.
        width: stackHeader ? "100%" : "auto",
      }}
    >
      <IconButton
        onClick={() => pageWeek(-1)}
        disabled={!canPageBack}
        aria-label="Previous week"
        size="small"
        sx={{ ...arrowSx, ...(isTouch && touchHitAreaSx(WEEK_ARROW_SIZE)) }}
      >
        <ChevronLeftRounded sx={{ fontSize: 18 }} />
      </IconButton>
      <Box
        key={week[0] ?? "empty"}
        role="group"
        aria-label="Days of the week"
        sx={{
          ...slideInSx(slide),
          flex: stackHeader ? 1 : "0 0 auto",
          minWidth: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 0.25,
        }}
      >
        {week.map((date) => (
          <DayPill
            key={date}
            date={date}
            isToday={date === today}
            selected={date === activeDate}
            compact={isPhone}
            touch={isTouch}
            onSelect={goTo}
          />
        ))}
      </Box>
      <IconButton
        onClick={() => pageWeek(1)}
        disabled={!canPageForward}
        aria-label="Next week"
        size="small"
        sx={{ ...arrowSx, ...(isTouch && touchHitAreaSx(WEEK_ARROW_SIZE)) }}
      >
        <ChevronRightRounded sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );

  const title = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <CalendarMonthOutlined
        sx={{ fontSize: 14, color: "#22c55e", flexShrink: 0 }}
      />
      <Typography
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          letterSpacing: "0.01em",
          color: theme.palette.text.primary,
          whiteSpace: "nowrap",
        }}
      >
        Economic Calendar
      </Typography>
    </Box>
  );

  const closeButton = (
    <IconButton
      onClick={onClose}
      aria-label="Close"
      size="small"
      sx={{
        width: isPhone ? 40 : 32,
        height: isPhone ? 40 : 32,
        mr: -0.75,
        color: theme.palette.text.secondary,
      }}
    >
      <CloseRounded sx={{ fontSize: 18 }} />
    </IconButton>
  );

  // Everything inside the frame is shared; only the frame differs. On a
  // phone the calendar is a bottom sheet — it rises from the tab bar's
  // edge and swipes down to close, the reachable gesture on a small screen —
  // where the desktop keeps the centred dialog.
  //
  // The whole surface opts out of the app's edge-swipe and pull-to-refresh
  // gestures: a layer over the page must never navigate the page underneath.
  const surface = (
      <Box
        data-native-gesture-optout=""
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          pt: 0,
        }}
      >
        {/* The header: one line — name, week, close — with the strip
            centred on the PANEL rather than on the space beside the name.
            The one hairline in the whole surface sits under it. */}
        {!stackHeader && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              gap: 2,
              px: 2.5,
              py: 1,
              flexShrink: 0,
              borderBottom: `1px solid ${hairline}`,
            }}
          >
            {title}
            {strip}
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", minWidth: 0 }}
            >
              {closeButton}
            </Box>
          </Box>
        )}
        {stackHeader && (
          <Box
            sx={{
              px: 2,
              pt: isPhone ? 0.75 : 1,
              pb: 1,
              flexShrink: 0,
              borderBottom: `1px solid ${hairline}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 0.75,
              }}
            >
              {title}
              {closeButton}
            </Box>
            {strip}
          </Box>
        )}

        {/* The body. Re-keyed on every step so it rises into place and the
            scroll starts at the top. */}
        <Box
          key={activeDate}
          onTouchStart={isTouch ? onTouchStart : undefined}
          onTouchEnd={isTouch ? onTouchEnd : undefined}
          sx={{
            ...riseIn(0),
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
              px: 3,
              pt: 3,
              pb: 2.5,
              flexShrink: 0,
            }}
          >
            <Box sx={{ minWidth: 0, mr: "auto" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: "1.0625rem",
                    fontWeight: 600,
                    lineHeight: 1.2,
                    letterSpacing: "-0.01em",
                    color: theme.palette.text.primary,
                    whiteSpace: "nowrap",
                  }}
                >
                  {dayjs.utc(activeDate).format("dddd D MMMM")}
                </Typography>
                {isToday && (
                  <Typography
                    sx={{
                      fontSize: "0.5625rem",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      lineHeight: "16px",
                      color: "#22c55e",
                      backgroundColor: "rgba(34, 197, 94, 0.1)",
                      px: 0.625,
                      borderRadius: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    Today
                  </Typography>
                )}
              </Box>
              <Typography
                sx={{
                  mt: 0.375,
                  fontSize: "0.6875rem",
                  color: theme.palette.text.secondary,
                }}
              >
                {subLine}
              </Typography>
            </Box>
            {total > 0 && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                  p: 0.25,
                  flexShrink: 0,
                  backgroundColor: isDark ? "#212121" : "#f5f5f5",
                  border: `1px solid ${panelBorder(isDark)}`,
                  borderRadius: 1,
                }}
              >
                {IMPACT_TIERS.map((tier) => (
                  <TierChip
                    key={tier}
                    tier={tier}
                    count={counts[tier]}
                    on={tiers.has(tier)}
                    locked={tier === "High" || escalated.includes(tier)}
                    touch={isTouch}
                    onToggle={toggleTier}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              px: 3,
              pb: isPhone ? "max(env(safe-area-inset-bottom), 20px)" : 0,
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {shown.map((tier) => (
              <TierSection
                key={tier}
                tier={tier}
                events={byTier[tier]}
                pulse={isToday}
              />
            ))}
            {total === 0 && (
              <Box
                sx={{
                  minHeight: 160,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  borderRadius: 1.5,
                  border: `1px solid ${hairline}`,
                }}
              >
                <CalendarMonthOutlined
                  sx={{ fontSize: 22, color: theme.palette.text.disabled }}
                />
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: theme.palette.text.secondary,
                  }}
                >
                  Nothing scheduled for this day
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
  );

  if (isPhone) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        disableSwipeToOpen
        disableBackdropTransition={isNative()}
        disableDiscovery={isNative()}
        transitionDuration={reduceMotion ? 0 : { enter: 260, exit: 200 }}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") stepDay(-1);
          if (e.key === "ArrowRight") stepDay(1);
        }}
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" } },
        }}
        PaperProps={{
          sx: {
            height: "92vh",
            "@supports (height: 1dvh)": { height: "92dvh" },
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            backgroundColor: overlaySurface(isDark),
            backgroundImage: "none",
            boxShadow: "none",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Grab handle — the swipe-to-dismiss affordance, in the house sheet's idiom. */}
        <Box sx={{ display: "flex", justifyContent: "center", flexShrink: 0, pt: 1.5, pb: 0.5 }}>
          <Box
            sx={{
              width: 36,
              height: 4,
              borderRadius: 2,
              backgroundColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
            }}
          />
        </Box>
        {surface}
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isPhone}
      maxWidth={false}
      TransitionComponent={Grow}
      transitionDuration={reduceMotion ? 0 : { enter: 240, exit: 160 }}
      TransitionProps={{ style: { transformOrigin: "center 35%" } }}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") stepDay(-1);
        if (e.key === "ArrowRight") stepDay(1);
      }}
      slotProps={{
        // Lighter than the app's usual black-out: the dashboard stays
        // legible behind, so this reads as a layer over the page rather
        // than a context switch.
        backdrop: {
          sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
          timeout: reduceMotion ? 0 : { enter: 220, exit: 160 },
        },
        paper: {
          sx: {
            backgroundColor: overlaySurface(isDark),
            backgroundImage: "none",
            border: isPhone ? "none" : `1px solid ${panelBorder(isDark)}`,
            borderRadius: isPhone ? 0 : 3,
            boxShadow: "none",
            // Desktop: wide enough for three cards abreast, and a FIXED
            // size, so stepping through the days moves the content and
            // never the frame.
            ...(!isPhone && {
              width: "min(1080px, calc(100vw - 64px))",
              height: "min(680px, calc(100dvh - 64px))",
              m: 0,
            }),
          },
        },
      }}
    >
      {surface}
    </Dialog>
  );
};

export default EconomicCalendarModal;
